export const prefix = "/api/v1"; // import.meta.env.VITE_API_URL;
const enableDebug = import.meta.env.VITE_IS_DEBUG;

import { navigate } from "wouter/use-browser-location";
import { notifications } from "@mantine/notifications";
import { slugify } from "./_Shared/slugify";
import mixpanel from "mixpanel-browser";
import { getGlobalStoreInstance } from "./store";
import { getI18n } from "react-i18next";

const jsonHeaders = {
  "content-type": "application/json",
  accept: "application/json",
};

// The UI language, so the server can write mails (activation, reset, invitation) in it. Without
// it the browser sends its own default, which may differ from the language chosen in the app.
export function uiLanguageHeaders() {
  try {
    const i18n = getI18n();
    const lng = i18n?.resolvedLanguage || i18n?.language;
    return lng ? { "accept-language": lng } : {};
  } catch {
    return {};
  }
}

export function getAuthHeaders() {
  const authToken = getGlobalStoreInstance().getState().authToken;
  if (authToken) return { authorization: `Bearer ${authToken}` };
  return {};
}

// Runs a request that streams JSON objects, invoking callback for each object
export async function jsonApiRequestStreaming(method, uri, values, callback) {
  const authHeader = getAuthHeaders();

  var reqDetails = {
    url: `${prefix}/${uri}`,
    method: method,
    headers: {
      ...jsonHeaders,
      ...authHeader,
    },
    body: values ? JSON.stringify(values) : null,
  };

  if (enableDebug) console.debug("executing STREAMING request", reqDetails);

  const response = await fetch(reqDetails.url, {
    method: reqDetails.method,
    headers: reqDetails.headers,
    body: reqDetails.body,
  });

  if (response.status === 401 && authHeader.authorization) {
    console.warn("Unauthorized request, logging out user");
    userLogout(true);
    return null;
  }

  if (!response.ok) {
    const message = await showRequestError(response);
    throw new Error(message);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");

    buffer = lines.pop(); // Keep the last incomplete line in the buffer

    for (const line of lines) {
      if (line.trim() !== "") {
        try {
          if (enableDebug) console.debug("Received STREAMING line:", line);
          const json = JSON.parse(line);
          if (enableDebug) console.log("Received STREAMING JSON object:", json);

          callback(json);
        } catch (error) {
          console.error("Error parsing JSON:", error);
        }
      }
    }
  }

  // Process any remaining data in the buffer
  if (buffer.trim() !== "") {
    try {
      const json = JSON.parse(buffer);
      if (enableDebug) console.log("Received FINAL JSON object:", json);
      callback(json);
    } catch (error) {
      console.error("Error parsing JSON:", error);
    }
  }
}

async function showRequestError(response) {
  let responseText = await response.text();
  let message = `${response.statusText} (${response.status}): ${responseText}`;

  if (enableDebug) console.debug("response text", responseText);

  let responseJson = null;
  try {
    if (responseText && responseText.length > 0)
      responseJson = JSON.parse(responseText);
    if (enableDebug) console.debug("responseJson", responseJson);
  } catch (parseError) {
    if (enableDebug)
      console.debug("Failed to parse response as JSON", parseError);
  }

  if (responseJson) {
    notifications.show({
      color: "red",
      autoClose: 10_000,
      title: responseJson.title || "An error occurred!",
      // `{ message }` is what BadRequest(new { message }) / NotFound(new { message }) send.
      message: responseJson.detail || responseJson.message || responseText,
    });
  } else {
    // notifications.show({
    //   color: "red",
    //   autoClose: 5000,
    //   title: "An error occurred!",
    //   message: message,
    // });
  }

  return message;
}

export async function apiRequest(method, uri, values) {
  const authHeader = getAuthHeaders();

  var reqDetails = {
    url: `${prefix}/${uri}`,
    method: method,
    headers: {
      ...jsonHeaders,
      ...uiLanguageHeaders(),
      ...authHeader,
    },
    body: values ? JSON.stringify(values) : null,
  };
  const response = await fetch(reqDetails.url, {
    method: reqDetails.method,
    headers: reqDetails.headers,
    body: reqDetails.body,
  });

  if (response.status === 401 && authHeader.authorization) {
    console.warn("Unauthorized request, logging out user");
    userLogout(true);
    return null;
  }

  if (response.status === 402) {
    // Plan-gate response (feature locked / limit reached). Emit a global event so the
    // app can show an upgrade prompt, then throw a typed error instead of a raw notification.
    let payload = null;
    try {
      payload = await response.clone().json();
    } catch {
      /* non-JSON 402 — fall through with defaults */
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("upgrade-required", { detail: payload }));
    }
    const err = new Error(payload?.message || "Upgrade required for this action.");
    err.status = 402;
    err.code = payload?.code;
    err.upgradeUrl = payload?.upgradeUrl || "/plans";
    throw err;
  }

  if (!response.ok) {
    const message = await showRequestError(response);
    throw new Error(message);
  }

  return response;
}

export async function jsonApiRequest(method, uri, values) {
  const response = await apiRequest(method, uri, values);
  if (!response) return null;

  if (response.status === 204) return {};

  // parse the JSON response
  const json = await response.json();
  if (enableDebug) console.debug("response json", json);
  return json;
}

export async function jsonPost(uri, values) {
  return await jsonApiRequest("POST", uri, values);
}

export async function jsonGet(uri) {
  return await jsonApiRequest("GET", uri, null);
}

export async function jsonPut(uri, values) {
  return await jsonApiRequest("PUT", uri, values);
}

export async function jsonDelete(uri) {
  return await jsonApiRequest("DELETE", uri, null);
}

// Pages that work without a session: signup, activation, password reset, invitations.
// Apps add their own (e.g. a supplier-invitation page) with addPublicPathPrefixes.
const publicPathPrefixes = ["/auth/", "/invite/"];
export const addPublicPathPrefixes = (...prefixes) => publicPathPrefixes.push(...prefixes);

// Revoke the session on the server and delete the AuthToken cookie. The server checks that
// cookie before the bearer header, so without this a "logged out" browser stayed signed in for
// the server. Fire and forget: the browser state is cleared either way; keepalive lets the
// request finish while the page navigates.
function endServerSession() {
  try {
    fetch(`${prefix}/auth/logout`, {
      method: "POST",
      headers: getAuthHeaders(),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* no fetch (tests) */
  }
}

// TBD -- not really an API call... should move into store
export const userLogout = (isInvoluntary = false) => {
  endServerSession(); // before the token is cleared: it sends the bearer token too
  getGlobalStoreInstance().getState().setAuthToken(null);
  getGlobalStoreInstance().getState().setProfile(null);
  // Reset the forced-enrollment gate so a stale flag can't carry into the next sign-in on this browser.
  getGlobalStoreInstance().getState().clearMustEnrollPasskey();
  // An old token from an earlier visit expires on the first request. On a public page, drop it
  // quietly: sending the visitor to "/" would take them away from the signup form or the
  // activation link they just opened.
  if (
    isInvoluntary &&
    typeof window !== "undefined" &&
    publicPathPrefixes.some((p) => window.location.pathname.startsWith(p))
  ) {
    return;
  }
  navigate("/", { replace: true });
  if (isInvoluntary) {
    // TBD: debounce this
    try {
      mixpanel.track("User logged out involuntarily");
    } catch {}
    notifications.show({
      color: "red",
      autoClose: 10_000,
      title: "You are logged out",
      message: "Please login again",
    });
  }
};

export const getAdminJobs = async (
  page,
  pageSize,
  tenantFilter,
  statusFilter
) =>
  await jsonGet(
    `admin-jobs?page=${page}&pageSize=${pageSize}&tenantFilter=${tenantFilter}&statusFilter=${statusFilter}`
  );

function encodeFileName(fileName) {
  // https://stackoverflow.com/questions/44429173/javascript-encodeuri-failed-to-encode-round-bracket
  return encodeURIComponent(fileName).replace(/[!'()*]/g, function (c) {
    return "%" + c.charCodeAt(0).toString(16);
  });
}

export async function uploadFile(file, type, marketplaceId = null) {
  let authHeaders = getAuthHeaders();

  let simpleFileName = slugify(file.name);
  let uriFileName = encodeFileName(file.name);

  var response = await fetch(
    `${prefix}/imports/${type}/${marketplaceId ?? ""}`,
    {
      method: "POST",
      body: file,
      headers: {
        ...authHeaders,
        "content-type": file.type,
        "content-length": `${file.size}`,
        "content-disposition": `attachment; filename=\"${simpleFileName}\"; filename*=UTF-8''${uriFileName}`,
      },
    }
  );

  if (!response.ok) {
    const message = await showRequestError(response);
    throw new Error(message);
  }
  return await response.json();
}

async function downloadResponseAsFile(response) {
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const contentDisposition = response.headers.get("Content-Disposition");
  const filenameMatch = contentDisposition?.match(
    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
  );
  const filename = filenameMatch
    ? filenameMatch[1].replace(/['"]/g, "")
    : "download";
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function downloadFile(uri) {
  const response = await apiRequest("GET", uri);
  return await downloadResponseAsFile(response);
}

// PROFILE
export const getProfile = async () => await jsonGet(`profile`);

export const updateProfile = async (dto) => {
  return await jsonPut(`profile`, dto);
};
// Timezones
export const getTimezones = async () => await jsonGet("timezones");

export const adminImpersonateUser = async (dto) =>
  await jsonPost(`auth/account/impersonate`, dto);

// TENANTS
export const createTenant = async (dto) => await jsonPost(`tenants`, dto);
export const updateTenant = async (id, dto) =>
  await jsonPut(`tenants/${id}`, dto);
export const getTenants = async (page, pageSize) =>
  await jsonGet(`tenants?page=${page}&pageSize=${pageSize}`);
export const getTenant = async (id) => await jsonGet(`tenants/${id}`);

// CREDITS
export const modifyCredits = async (tenantId, dto) => {
  // mix.track("Modify credits", { tenantId });
  return await jsonPost(`tenants/${tenantId}/credits`, dto);
};

export const getAdminJobDetails = async (id) =>
  await jsonGet(`admin-jobs/${id}`);
export const requeueJob = async (id) =>
  await jsonPost(`admin-jobs/${id}/retry`);
export const requeueJobHard = async (id) =>
  await jsonPost(`admin-jobs/${id}/retry-hard`);
export const requeueJobs = async (jobIds, hard = false) =>
  await jsonPost(`admin-jobs/retry`, { jobIds, hard });

export const getTenantProfile = async () => await jsonGet(`tenant/profile`);
export const updateTenantProfile = async (dto) =>
  await jsonPut(`tenant/profile`, dto);

// API KEYS
export const getTenantApikeys = async () => await jsonGet(`tenant/apikeys`);
export const createApiKey = async (dto) => {
  try {
    mixpanel.track("API Key Created");
  } catch (e) {}
  return await jsonPost(`tenant/apikeys`, dto);
};
export const deleteApiKey = async (id) =>
  await jsonDelete(`tenant/apikeys/${id}`);

// CHECKOUT & PAYMENTS
// export async function getProducts() {
//   return await jsonGet(`payment/stripe/products`);
// }
export async function checkoutSubscription(dto) {
  return await jsonPost(`payment/stripe/checkout-plan`, dto);
}
export async function checkoutCredits(dto) {
  return await jsonPost("payment/stripe/checkout-credits", dto);
}
export async function customerPortal() {
  // mixpanel.track("Customer Portal");
  return await jsonPost("payment/stripe/portal");
}

export function checkout(plan, callback) {
  checkoutSubscription(plan)
    .then((data) => {
      mixpanel.track("Checkout Plan", {
        plan: plan.planName,
        interval: plan.interval,
      });
      if (callback) callback();
      location.href = data.redirectUrl;
    })
    .catch((error) => {
      alert("Sorry, something went wrong: " + error);
      if (callback) callback();
    });
}

// USERS
export const getUsers = async (page, pageSize) =>
  await jsonGet(`users?page=${page}&pageSize=${pageSize}`);
export const getSingleUser = async (id) => await jsonGet(`users/${id}`);
export const createUser = async (dto) => await jsonPost(`auth/account`, dto);
export const updateUser = async (id, dto) => await jsonPut(`users/${id}`, dto);
export const deleteUser = async (id) => await jsonDelete(`users/${id}`);
// Admin recovery for a lost passkey: clears the user's passkeys so they can enroll a fresh one.
export const resetUserPasskeys = async (id) =>
  await jsonDelete(`auth/passkey/user/${id}`);

// INVITATIONS
export const sendUserInvitation = async (id) =>
  await jsonPost(`users/${id}/invitations`);

export async function uploadImage(file, imageType) {
  let authHeaders = getAuthHeaders();
  var response = await fetch(`${prefix}/profile/images/${imageType}`, {
    method: "POST",
    body: file,
    headers: {
      ...authHeaders,
      "content-type": file.type,
      "content-length": `${file.size}`,
    },
  });
  // mixpanel.track("Upload image", { imageType });
  return await response.json();
}
